import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import useGoalInfo from '@api/hooks/goal/useGoalInfo';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useTestTabCards from '@api/hooks/goal/test-tab/useTestTabCards';
import pushRoute from '@utils/pushRoute';
import { appendQuizReturnTo } from '@utils/quizReturnPath';
import usePrimeTrialPromoModal from '@stores/prime-pitch/usePrimeTrialPromoModal';
import { PrimeTrialPromoContextualId } from '@stores/prime-pitch/types';
import useTestTabFilterMetadata from '@api/hooks/goal/test-tab/useTestTabFilterMetadata';
import { getCustomPageDetails } from '@utils/getCustomPageDetails';
import { ALL_TEST_SERIES } from '@constants/segment';
import PrimeBottomBanner from '@cont/PrimeHome/PrimeBottomBanner';
import { BottomSpacer } from '@cont/PrimeHome/PrimeHome.styles';
import {
  buildTestTabBaseProps,
  saveTestTabAnalyticsContext,
  trackTestTestSeriesViewed,
  trackTestTestsSectionClicked,
  trackTestTestsSubSectionClicked
} from './testTab/testTabAnalytics';
import useTestListingLockState from './testTab/useTestListingLockState';

import TopBar from './Common/TopBar';
import { TEST_SERIES_TAB } from './Common/TestSeriesTabSwitcher';
import Breadcrumbs from './Common/Breadcrumbs';
import TestSeriesListingTestCardsGrid from './Common/TestSeriesListingTestCardsGrid';
import DailyScholarshipTest from './DailyScholarshipTest/DST';
import { getDefaultCategoryTab } from './Common/TestSeriesContentPanel';
import { buildFilterRequestBody } from './testTab/buildFilterRequestBody';
import { getApiTabId } from './testTab/constants';
import { createEmptyFilterSelections } from './testTab/parseFilterMetadata';

const DEFAULT_LANGUAGE = 1;

const getQueryParam = (value) => (Array.isArray(value) ? value[0] : value);

const TestSeriesListing = ({ goalName: goalNameProp = '' }) => {
  const { query, asPath } = useRouter();
  const goalUID = getQueryParam(query.goalUID);
  const goalSlug = getQueryParam(query.goalSlug);
  const { goalInfo } = useGoalInfo(goalUID);
  const isLoggedIn = useMyInfo(getIsLoggedIn);
  const { openElement: showLoginDrawer } = useToggleable(LOGIN_DRAWER);
  const { openPrimeTrialPromoModal } = usePrimeTrialPromoModal();
  const { showPrimeStickyBanner, testQuotaLeft, testQuotaTotal } =
    useTestListingLockState(goalUID);

  const goalDisplayName =
    goalNameProp || goalInfo?.name || goalInfo?.title || '';

  const pageDetails = useMemo(
    () =>
      getCustomPageDetails(
        goalDisplayName,
        goalInfo?.title || goalDisplayName,
        ALL_TEST_SERIES
      ),
    [goalDisplayName, goalInfo?.title]
  );
  const [activeTestTab, setActiveTestTab] = useState(TEST_SERIES_TAB.ALL);
  const [activeCategoryTab, setActiveCategoryTab] = useState(() =>
    getDefaultCategoryTab(TEST_SERIES_TAB.ALL)
  );
  const [appliedFilters, setAppliedFilters] = useState(
    createEmptyFilterSelections
  );

  const apiTabId = getApiTabId(activeTestTab, activeCategoryTab);

  const { filterConfig } = useTestTabFilterMetadata({
    goalUid: goalUID,
    apiTabId,
    language: DEFAULT_LANGUAGE,
    view: activeTestTab,
    categoryTabId: activeCategoryTab,
    isLoggedIn
  });

  const filterBody = useMemo(() => {
    if (!apiTabId) {
      return null;
    }

    return buildFilterRequestBody({
      apiTabId,
      categoryTabId: activeCategoryTab,
      appliedSelections: appliedFilters,
      isLoggedIn
    });
  }, [activeCategoryTab, apiTabId, appliedFilters, isLoggedIn]);

  const {
    cards,
    isInitialLoading,
    isRefreshing,
    isError,
    hasMore,
    loadMore,
    isValidating
  } = useTestTabCards({
    goalUid: goalUID,
    view: activeTestTab,
    categoryTabId: activeCategoryTab,
    filterBody,
    language: DEFAULT_LANGUAGE,
    isLoggedIn
  });

  const getBaseTestTabProps = useCallback(
    (
      view = activeTestTab,
      categoryTabId = activeCategoryTab,
      filters = appliedFilters
    ) =>
      buildTestTabBaseProps({
        view,
        categoryTabId,
        appliedSelections: filters
      }),
    [activeCategoryTab, activeTestTab, appliedFilters]
  );

  const handleTestTabChange = useCallback(
    (nextTab) => {
      if (nextTab === TEST_SERIES_TAB.MY && !isLoggedIn) {
        showLoginDrawer();
        return;
      }

      const nextCategoryTab = getDefaultCategoryTab(nextTab);
      const baseProps = buildTestTabBaseProps({
        view: nextTab,
        categoryTabId: nextCategoryTab,
        appliedSelections: createEmptyFilterSelections()
      });

      trackTestTestsSectionClicked({
        goalUid: goalUID,
        goalName: goalDisplayName,
        ...baseProps
      });
      saveTestTabAnalyticsContext(baseProps);

      setActiveTestTab(nextTab);
      setActiveCategoryTab(nextCategoryTab);
      setAppliedFilters(createEmptyFilterSelections());
    },
    [goalDisplayName, goalUID, isLoggedIn, showLoginDrawer]
  );

  const handleCategoryTabChange = useCallback(
    (nextCategoryTab) => {
      const baseProps = buildTestTabBaseProps({
        view: activeTestTab,
        categoryTabId: nextCategoryTab,
        appliedSelections: createEmptyFilterSelections()
      });

      trackTestTestsSubSectionClicked({
        goalUid: goalUID,
        goalName: goalDisplayName,
        ...baseProps
      });
      saveTestTabAnalyticsContext(baseProps);

      setActiveCategoryTab(nextCategoryTab);
      setAppliedFilters(createEmptyFilterSelections());
    },
    [activeTestTab, goalDisplayName, goalUID]
  );

  const handleAppliedSelectionsChange = useCallback(
    (nextSelections) => {
      saveTestTabAnalyticsContext(
        buildTestTabBaseProps({
          view: activeTestTab,
          categoryTabId: activeCategoryTab,
          appliedSelections: nextSelections
        })
      );
      setAppliedFilters(nextSelections);
    },
    [activeCategoryTab, activeTestTab]
  );

  const handleTestCardClick = useCallback(
    (card) => {
      if (card.lockType === 'prime' && isLoggedIn) {
        openPrimeTrialPromoModal(
          PrimeTrialPromoContextualId.TEST,
          goalUID,
          goalSlug,
          {
            dismissNavigateTo: null,
            analyticsContext: {
              testUid: card.variant === 'quiz' ? card.id : undefined
            }
          }
        );
        return;
      }

      const baseProps = getBaseTestTabProps();

      if (card.variant === 'test_series') {
        trackTestTestSeriesViewed({
          goalUid: goalUID,
          goalName: goalDisplayName,
          ...baseProps,
          test_series_title: card.title,
          test_series_uid: card.id
        });
        saveTestTabAnalyticsContext({
          ...baseProps,
          test_series_title: card.title,
          test_series_uid: card.id
        });
      } else {
        saveTestTabAnalyticsContext({
          ...baseProps,
          test_uid: card.id,
          test_name: card.title
        });
      }

      if (card.href) {
        const shouldAttachReturnPath =
          card.variant === 'quiz' && card.href.startsWith('/quiz');

        pushRoute(
          shouldAttachReturnPath
            ? appendQuizReturnTo(card.href, asPath)
            : card.href
        );
      }
    },
    [
      asPath,
      getBaseTestTabProps,
      goalDisplayName,
      goalSlug,
      goalUID,
      isLoggedIn,
      openPrimeTrialPromoModal
    ]
  );

  return (
    <>
      <TopBar
        breadcrumbs={
          <Breadcrumbs
            type={Breadcrumbs.TYPE.ONLY_TEST_SERIES}
            goalDisplayName={goalDisplayName}
          />
        }
        afterBreadcrumbs={
          <DailyScholarshipTest
            goalUID={goalUID}
            goalSlug={goalSlug}
            goalName={goalDisplayName}
          />
        }
        title={pageDetails?.title}
        description={goalDisplayName ? pageDetails?.description : undefined}
        activeTab={activeTestTab}
        onTabChange={handleTestTabChange}
        activeCategoryTab={activeCategoryTab}
        onCategoryTabChange={handleCategoryTabChange}
        filterConfig={filterConfig}
        appliedSelections={appliedFilters}
        onAppliedSelectionsChange={handleAppliedSelectionsChange}
      />
      <TestSeriesListingTestCardsGrid
        cards={cards}
        isInitialLoading={isInitialLoading}
        isRefreshing={isRefreshing}
        isError={isError}
        hasMore={hasMore}
        onLoadMore={loadMore}
        isValidating={isValidating}
        onCardClick={handleTestCardClick}
      />
      {showPrimeStickyBanner && <BottomSpacer />}
      {showPrimeStickyBanner && (
        <PrimeBottomBanner
          title={
            testQuotaLeft <= 0
              ? 'Daily limit reached'
              : `${testQuotaLeft} out of ${testQuotaTotal} free tests left!`
          }
          subtitle="Get unlimited tests with Prime."
          contextualId={PrimeTrialPromoContextualId.TEST}
          goalUID={goalUID}
          goalSlug={goalSlug}
          analyticsPitchSource="Tests Home Sticky Bottomsheet"
        />
      )}
    </>
  );
};

export default TestSeriesListing;

TestSeriesListing.propTypes = {
  goalName: PropTypes.string
};
